<template>
  <main class="accesos-dashboard">
    <h3 class="d-none d-md-block">REGISTROS DE ACCESO</h3>
    <h3 class="d-md-none font-weight-bold">REGISTROS<br />DE ACCESO</h3>

    <!-- Descripción del módulo -->
    <div class="alert d-flex align-items-center mb-4" role="alert" style="background-color: #66C4DE; border-color: #66C4DE;">
      <i class="fas fa-info-circle fa-2x" style="margin-right: 15px; color: #fff;"></i>
      <div>
        <p class="mb-0" style="color: #353535;">
          <strong>Analiza el movimiento de personas por los puntos de acceso de tus sucursales.</strong>
          Consulta métricas y tendencias de ingreso, revisa el registro detallado de cada acceso y
          el resumen por persona, aplicando filtros homogéneos sobre todo el universo de datos.
        </p>
      </div>
    </div>

    <!-- Barra de filtros -->
    <div class="filtros-card mb-4">
      <div class="filtros-grid">
        <div class="filtro-field">
          <label>Desde</label>
          <input type="date" class="form-control" v-model="filters.dateFrom" />
        </div>
        <div class="filtro-field">
          <label>Hasta</label>
          <input type="date" class="form-control" v-model="filters.dateTo" />
        </div>
        <div class="filtro-field">
          <label>Sucursal</label>
          <select class="form-select" v-model="filters.branch">
            <option value="">Todas</option>
            <option v-for="s in catalogs.sucursales" :key="s" :value="s">{{ s }}</option>
          </select>
        </div>
        <div class="filtro-field">
          <label>Estado</label>
          <select class="form-select" v-model="filters.accessState">
            <option value="">Todos</option>
            <option v-for="e in catalogs.estadosAcceso" :key="e" :value="e">{{ e }}</option>
          </select>
        </div>
        <div class="filtro-field">
          <label>Método</label>
          <select class="form-select" v-model="filters.accessMethod">
            <option value="">Todos</option>
            <option v-for="m in catalogs.metodosAcceso" :key="m" :value="m">{{ m }}</option>
          </select>
        </div>
        <div class="filtro-field">
          <label>Perfil</label>
          <select class="form-select" v-model="filters.profile">
            <option value="">Todos</option>
            <option v-for="p in catalogs.perfilesPersona" :key="p" :value="p">{{ p }}</option>
          </select>
        </div>
        <div class="filtro-field">
          <label>Plan / servicio</label>
          <select class="form-select" v-model="filters.planAcceso">
            <option value="">Todos</option>
            <option v-for="p in catalogs.planesAcceso" :key="p" :value="p">{{ p }}</option>
          </select>
        </div>
        <div class="filtro-field">
          <label>Punto de acceso</label>
          <select class="form-select" v-model="filters.accessPoint">
            <option value="">Todos</option>
            <option v-for="p in catalogs.puntosAcceso" :key="p" :value="p">{{ p }}</option>
          </select>
        </div>
        <div class="filtro-field filtro-field--wide">
          <label>Búsqueda (nombre o RUT)</label>
          <input
            type="text"
            class="form-control"
            v-model="filters.searchText"
            placeholder="Nombre o 12345678-9"
            @keyup.enter="applyFilters"
          />
        </div>
      </div>

      <div class="filtros-actions">
        <button class="btn btn-outline-success" :disabled="loadingMetrics || exporting" @click="onExport">
          <i class="fas fa-file-excel me-1"></i> {{ exporting ? 'Exportando…' : 'Exportar a Excel' }}
        </button>
        <button class="btn btn-outline-secondary" :disabled="loadingMetrics || exporting" @click="clearFilters">
          <i class="fas fa-eraser me-1"></i> Limpiar
        </button>
        <button class="btn btn-success" :disabled="loadingMetrics || exporting" @click="applyFilters">
          <i class="fas fa-search me-1"></i> Aplicar filtros
        </button>
      </div>

      <div v-if="catalogsError" class="text-warning small mt-2">
        <i class="fas fa-exclamation-triangle"></i> No se pudieron cargar los catálogos de filtros. Puedes seguir usando el rango de fechas.
      </div>
    </div>

    <!-- Pestañas -->
    <ul class="nav nav-tabs accesos-tabs mb-3">
      <li class="nav-item" v-for="t in tabs" :key="t.key">
        <a
          class="nav-link"
          :class="{ active: activeTab === t.key }"
          href="#"
          @click.prevent="activeTab = t.key"
        >
          <i :class="t.icon" class="me-1"></i> {{ t.label }}
        </a>
      </li>
    </ul>

    <!-- Contenido de pestañas -->
    <div v-show="activeTab === 'metricas'">
      <accesos-metricas
        :metrics="metrics"
        :loading="loadingMetrics"
        :timezone="timezone"
      ></accesos-metricas>
    </div>

    <div v-show="activeTab === 'registros'">
      <accesos-registros
        :parameters="appliedParameters"
        :params-version="appliedVersion"
        :active="activeTab === 'registros'"
      ></accesos-registros>
    </div>

    <div v-show="activeTab === 'personas'">
      <accesos-personas
        :parameters="appliedParameters"
        :params-version="appliedVersion"
        :active="activeTab === 'personas'"
      ></accesos-personas>
    </div>
  </main>
</template>

<script>
import AccesosMetricas from './AccesosMetricas.vue';
import AccesosRegistros from './AccesosRegistros.vue';
import AccesosPersonas from './AccesosPersonas.vue';
import accesosExportMixin from '@/mixins/accesosExportMixin';

// Operaciones del Servicio.GestionDashboard — Análisis de Accesos (ver
// "Documentación Proyecto/Log de Accesos/ENDPOINT-LOG-ACCESO-DASHBOARD.md").
const OP_CATALOGS = '/v1/access/get/gymaccessviewcatalogs';
const OP_METRICS = '/v1/access/get/gymaccess/metrics';

export default {
  name: 'AccesosDashboard',
  components: { AccesosMetricas, AccesosRegistros, AccesosPersonas },
  mixins: [accesosExportMixin],
  data() {
    return {
      activeTab: 'metricas',
      tabs: [
        { key: 'metricas', label: 'Métricas', icon: 'fas fa-chart-pie' },
        { key: 'registros', label: 'Registros', icon: 'fas fa-table' },
        { key: 'personas', label: 'Personas', icon: 'fas fa-users' }
      ],
      catalogs: {
        sucursales: [],
        metodosAcceso: [],
        perfilesPersona: [],
        planesAcceso: [],
        puntosAcceso: [],
        estadosAcceso: []
      },
      catalogsError: false,
      filters: {
        dateFrom: '',
        dateTo: '',
        branch: '',
        accessState: '',
        accessMethod: '',
        profile: '',
        planAcceso: '',
        accessPoint: '',
        searchText: ''
      },
      metrics: null,
      timezone: '',
      loadingMetrics: false,
      // Snapshot de filtros comprometido (compartido 1:1 entre métricas, registros
      // y, en la fase siguiente, personas). appliedVersion se incrementa en cada
      // aplicación para que las pestañas sepan que deben recargar.
      appliedParameters: {},
      appliedVersion: 0
    };
  },
  created() {
    this.setDefaultDateRange();
  },
  mounted() {
    this.loadCatalogs();
    this.applyFilters();
  },
  methods: {
    // El backend espera fechas en DD-MM-YYYY; los inputs nativos entregan YYYY-MM-DD.
    toApiDate(isoDate) {
      if (!isoDate) return '';
      const [y, m, d] = isoDate.split('-');
      if (!y || !m || !d) return '';
      return `${d}-${m}-${y}`;
    },

    setDefaultDateRange() {
      const today = new Date();
      const from = new Date();
      from.setDate(today.getDate() - 29); // últimos 30 días (inclusive)
      const iso = (date) => {
        const y = date.getFullYear();
        const m = String(date.getMonth() + 1).padStart(2, '0');
        const d = String(date.getDate()).padStart(2, '0');
        return `${y}-${m}-${d}`;
      };
      this.filters.dateFrom = iso(from);
      this.filters.dateTo = iso(today);
    },

    // Construye el objeto Parameters unificado (sección 2 del contrato).
    // Se comparte 1:1 entre registros, métricas y personas.
    buildParameters() {
      const f = this.filters;
      const p = {};
      const from = this.toApiDate(f.dateFrom);
      const to = this.toApiDate(f.dateTo);
      if (from) p.dateFrom = from;
      if (to) p.dateTo = to;
      if (f.branch) p.branch = f.branch;
      if (f.accessState) p.accessState = f.accessState;
      if (f.accessMethod) p.accessMethod = f.accessMethod;
      if (f.profile) p.profile = f.profile;
      if (f.planAcceso) p.planAcceso = f.planAcceso;
      if (f.accessPoint) p.accessPoint = f.accessPoint;
      if (f.searchText && f.searchText.trim()) p.searchText = f.searchText.trim();
      return p;
    },

    async loadCatalogs() {
      this.catalogsError = false;
      try {
        const response = await this.$CentralGymOPS_API.post('', {
          Header: { Operacion: OP_CATALOGS },
          Auth: { Token: sessionStorage.getItem('jCxbraoQeD') },
          Parameters: {},
          Body: {}
        });
        const result = response.data && response.data.result;
        if (result) {
          this.catalogs = {
            sucursales: result.sucursales || [],
            metodosAcceso: result.metodosAcceso || [],
            perfilesPersona: result.perfilesPersona || [],
            planesAcceso: result.planesAcceso || [],
            puntosAcceso: result.puntosAcceso || [],
            estadosAcceso: result.estadosAcceso || []
          };
        }
      } catch (error) {
        this.catalogsError = true;
        this.$logger.error('Error cargando catálogos de accesos:', error);
      }
    },

    async fetchMetrics() {
      this.loadingMetrics = true;
      try {
        const response = await this.$CentralGymOPS_API.post('', {
          Header: { Operacion: OP_METRICS },
          Auth: { Token: sessionStorage.getItem('jCxbraoQeD') },
          Parameters: this.appliedParameters,
          Body: {}
        });
        const result = response.data && response.data.result;
        this.metrics = result || null;
        this.timezone = (result && result.meta && result.meta.timezone) || '';
      } catch (error) {
        this.metrics = null;
        this.$logger.error('Error obteniendo métricas de accesos:', error);
        this.handleRequestError(error, 'No se pudieron obtener las métricas de acceso.');
      } finally {
        this.loadingMetrics = false;
      }
    },

    applyFilters() {
      // Compromete los filtros actuales en un snapshot único y notifica a las
      // pestañas (vía appliedVersion). Las métricas se recargan siempre; los
      // registros se recargan de forma perezosa cuando su pestaña está activa.
      this.appliedParameters = this.buildParameters();
      this.appliedVersion++;
      this.fetchMetrics();
    },

    onExport() {
      // Exporta todo el universo filtrado (no sólo la página visible) usando los
      // filtros actualmente seleccionados. El mixin itera GetGymAccessReportFormat.
      this.exportAccesosToExcel(this.buildParameters());
    },

    clearFilters() {
      this.filters.branch = '';
      this.filters.accessState = '';
      this.filters.accessMethod = '';
      this.filters.profile = '';
      this.filters.planAcceso = '';
      this.filters.accessPoint = '';
      this.filters.searchText = '';
      this.setDefaultDateRange();
      this.applyFilters();
    },

    handleRequestError(error, fallbackMsg) {
      // El interceptor de axios ya gestiona el 401 (sesión expirada). Aquí sólo
      // mostramos errores de validación de filtros (400) u otros.
      const status = error && error.response && error.response.status;
      if (status === 401) return;
      let message = fallbackMsg;
      const data = error && error.response && error.response.data;
      if (data && data.error) {
        try {
          const parsed = typeof data.error === 'string' ? JSON.parse(data.error) : data.error;
          if (parsed && parsed.message) message = parsed.message;
        } catch (e) {
          if (typeof data.error === 'string') message = data.error;
        }
      }
      this.$swal.fire({
        icon: status === 400 ? 'warning' : 'error',
        title: status === 400 ? 'Filtros inválidos' : 'Error',
        text: message,
        confirmButtonColor: '#6bd098'
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.filtros-card {
  background: #fff;
  border-radius: 14px;
  padding: 22px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
}

/* Grid uniforme de filtros: campos de igual ancho que se reordenan en filas
   regulares (independiente del grid de Bootstrap, que entra en conflicto con
   Paper Dashboard y dejaba las columnas desalineadas). */
.filtros-grid {
  display: grid;
  gap: 16px;
  grid-template-columns: repeat(auto-fill, minmax(190px, 1fr));
  align-items: end;
}

.filtro-field {
  display: flex;
  flex-direction: column;
  min-width: 0;

  label {
    font-size: 12px;
    font-weight: 600;
    color: #66615b;
    margin-bottom: 6px;
  }

  .form-control,
  .form-select {
    height: 40px;
  }
}

/* El campo de búsqueda ocupa el doble de ancho cuando hay espacio. */
.filtro-field--wide {
  grid-column: span 2;
}

.filtros-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 20px;
  padding-top: 18px;
  border-top: 1px solid #f0f0f0;

  .btn {
    min-width: 150px;
  }
}

@media (max-width: 575.98px) {
  .filtros-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .filtro-field--wide {
    grid-column: span 2;
  }

  .filtros-actions {
    flex-direction: column-reverse;

    .btn {
      width: 100%;
    }
  }
}

.accesos-tabs {
  border-bottom: 2px solid #e9ecef;

  .nav-link {
    color: #66615b;
    font-weight: 600;
    border: none;
    border-bottom: 3px solid transparent;
    cursor: pointer;

    &.active {
      color: #6bd098;
      background: transparent;
      border-bottom-color: #6bd098;
    }
  }
}
</style>
